import React, { useEffect, useMemo, useState, useCallback } from "react";
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BadgeIcon from '@mui/icons-material/Badge';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EditIcon from '@mui/icons-material/Edit';
import { FieldInput } from '../../../ui/FieldInput';
import { AppPagination } from '../../../ui/AppPagination';
import { Organization } from "./OrganizationsManager";

type ApiSpecialistRead = {
  id: number;
  organization_id: number;
  full_name: string;
  phone: string | null;
  email: string | null;
  created_at: string;
  updated_at: string;
};

type ApiSpecialistListResponse = {
  total: number;
  page: number;
  size: number;
  pages: number;
  items: ApiSpecialistRead[];
};

export type Specialist = {
  id: string;
  organizationId: string;
  fullName: string;
  phone?: string;
  email?: string;
  createdAt: string;
  updatedAt: string;
};

type SpecialistForm = {
  fullName: string;
  phone: string;
  email: string;
  organizationId: string;
};

type Mode = "list" | "view" | "create" | "edit";

type Props = {
  organizationId?: string | null;
  organizations: Pick<Organization, 'id' | 'name'>[];
  onSelect?: (specialist: Specialist | null) => void;
  initialSelectedId?: string | null;
};

const PAGE_SIZE = 5;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("ru-RU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const emptyToNull = (v: string) => (v?.trim() ? v.trim() : null);

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1`;
const SPECIALISTS_URL = `${BASE_URL}/responsible_specialists/`;

const mapApiToUi = (s: ApiSpecialistRead): Specialist => ({
  id: String(s.id),
  organizationId: String(s.organization_id),
  fullName: s.full_name,
  phone: s.phone ?? undefined,
  email: s.email ?? undefined,
  createdAt: s.created_at,
  updatedAt: s.updated_at,
});

const defaultForm = (orgId?: string | null): SpecialistForm => ({
  fullName: "",
  phone: "",
  email: "",
  organizationId: orgId ?? "",
});

const SpecialistListItem: React.FC<{
  specialist: Specialist;
  onClick: () => void;
  isSelected: boolean;
}> = ({ specialist, onClick, isSelected }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left rounded-lg border p-4 shadow-sm transition-all focus:outline-none ${
      isSelected
        ? "bg-blue-50 border-blue-400 shadow-md"
        : "bg-white border-gray-200 hover:border-blue-400 hover:shadow-md"
    }`}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <BadgeIcon className={isSelected ? "text-blue-600" : "text-gray-400"} />
        <div className="flex flex-col">
          <span
            className={`font-medium ${isSelected ? "text-blue-800" : "text-gray-800"}`}
          >
            {specialist.fullName}
          </span>
          <span className="text-xs text-gray-500">
            {specialist.email || specialist.phone || "Нет контактов"}
          </span>
        </div>
      </div>
      <ChevronRightIcon className={isSelected ? "text-blue-600" : "text-gray-400"} />
    </div>
  </button>
);

export const ResponsibleSpecialistsManager: React.FC<Props> = ({
  organizationId = null,
  organizations,
  onSelect,
  initialSelectedId,
}) => {
  const [mode, setMode] = useState<Mode>("list");
  const [search, setSearch] = useState("");
  const [specialists, setSpecialists] = useState<Specialist[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Specialist | null>(null);

  const [formData, setFormData] = useState<SpecialistForm>(defaultForm(organizationId));
  const [errors, setErrors] = useState<Partial<Record<keyof SpecialistForm, string>>>({});
  const [flash, setFlash] = useState<string>("");

  const [cachedOrgName, setCachedOrgName] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);

  useEffect(() => {
    const idToFind = formData.organizationId || organizationId;
    if (idToFind) {
      const found = organizations.find((o) => String(o.id) === String(idToFind));
      if (found) {
        setCachedOrgName(found.name);
      }
    } else {
      setCachedOrgName("");
    }
  }, [formData.organizationId, organizationId, organizations]);

  const fetchPage = useCallback(
    async (orgId: string, targetPage: number) => {
      const token = localStorage.getItem("token");
      if (!token) return;

      setLoading(true);
      setLoadError(null);

      try {
        const params = new URLSearchParams({
          page: String(targetPage),
          size: String(PAGE_SIZE),
        });

        const url = `${BASE_URL}/organizations/${orgId}/responsible_specialists/?${params}`;

        const res = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: token,
          },
        });

        if (!res.ok) {
          throw new Error(`Ошибка загрузки: ${res.status} ${res.statusText}`);
        }

        const data: ApiSpecialistListResponse = await res.json();
        const mapped = (data.items ?? []).map(mapApiToUi);

        setSpecialists(mapped);
        setTotalItems(data.total);
        setTotalPages(data.pages);

        if (initialSelectedId) {
          const found = mapped.find((s) => s.id === initialSelectedId);
          if (found) {
            setSelected(found);
            onSelect?.(found);
          }
        }
      } catch (error: any) {
        console.error(error);
        setLoadError(error?.message || "Не удалось загрузить специалистов.");
      } finally {
        setLoading(false);
      }
    },
    [initialSelectedId, onSelect]
  );

  useEffect(() => {
    setMode("list");
    setSelected(null);
    onSelect?.(null);
    setSearch("");
    setPage(1);
    setTotalItems(0);
    setTotalPages(1);
    setErrors({});
    setLoadError(null);
    setSpecialists([]);
    setFormData(defaultForm(organizationId));

    if (organizationId) {
      fetchPage(organizationId, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;
    fetchPage(organizationId, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const displayedSpecialists = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return specialists;
    return specialists.filter((s) =>
      [s.fullName, s.phone, s.email]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [search, specialists]);

  const openView = (specialist: Specialist) => {
    setSelected(specialist);
    setMode("view");
    setErrors({});
    onSelect?.(specialist);
  };

  const openCreate = () => {
    setSelected(null);
    setFormData(defaultForm(organizationId));
    setErrors({});
    setMode("create");
    onSelect?.(null);
  };

  const openEdit = () => {
    if (!selected) return;
    setFormData({
      fullName: selected.fullName,
      phone: selected.phone || "",
      email: selected.email || "",
      organizationId: selected.organizationId,
    });
    setErrors({});
    setMode("edit");
  };

  const closePanel = () => {
    setMode("list");
  };

  const handleFormChange = (field: keyof SpecialistForm, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (v: SpecialistForm) => {
    const e: Partial<Record<keyof SpecialistForm, string>> = {};
    if (!v.fullName.trim()) e.fullName = "Укажите ФИО";
    if (v.email && !/\S+@\S+\.\S+/.test(v.email)) e.email = "Некорректный email";
    if (!v.organizationId) e.organizationId = "Отсутствует привязка к организации";
    return e;
  };

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const eMap = validate(formData);
    setErrors(eMap);
    if (Object.keys(eMap).length > 0) return;

    const token = localStorage.getItem("token");
    const isEdit = mode === "edit";

    const payload = {
      organization_id: Number(formData.organizationId),
      full_name: formData.fullName.trim(),
      phone: emptyToNull(formData.phone),
      email: emptyToNull(formData.email),
    };

    const url = isEdit ? `${SPECIALISTS_URL}${selected!.id}` : SPECIALISTS_URL;
    const method = isEdit ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: token || "",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let message = `Ошибка ${isEdit ? "сохранения" : "создания"} (${res.status})`;
        try {
          const err = await res.json();
          message = err?.detail || JSON.stringify(err) || message;
        } catch {}
        throw new Error(message);
      }

      const savedData: ApiSpecialistRead = await res.json();
      const savedSpecialist = mapApiToUi(savedData);

      if (isEdit) {
        setSpecialists((prev) => prev.map((s) => (s.id === savedSpecialist.id ? savedSpecialist : s)));
        setSelected(savedSpecialist);
        setFlash("Специалист успешно обновлен");
        setMode("view");
      } else {
        setFlash("Специалист успешно создан");
        setPage(1);
        if (organizationId) {
          await fetchPage(organizationId, 1);
        }
        openView(savedSpecialist);
      }

      setTimeout(() => setFlash(""), 3000);
    } catch (error: any) {
      setLoadError(error?.message || "Не удалось сохранить специалиста.");
    }
  };

  const isPanelOpen = mode === "view" || mode === "create" || mode === "edit";

  return (
    <section className="bg-white border border-gray-200 rounded-lg shadow-sm w-full">
      <header className="flex items-center justify-between gap-4 p-4 sm:p-5 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <BadgeIcon className="text-gray-500" />
          <h1 className="text-lg font-medium text-gray-900">
            Ответственные специалисты
          </h1>
        </div>
        <button
          type="button"
          onClick={mode === "create" ? closePanel : openCreate}
          disabled={!organizationId && mode !== "create"}
          className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors focus:outline-none ${
            mode === "create"
              ? "bg-gray-600 hover:bg-gray-700"
              : !organizationId
              ? "bg-gray-400 text-gray-200 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {mode === "create" ? (
            <ArrowBackIcon fontSize="small" />
          ) : (
            <AddIcon fontSize="small" />
          )}
          {mode === "create" ? "К списку" : "Добавить специалиста"}
        </button>
      </header>

      <div className="p-4 sm:p-6">
        {flash && (
          <div
            className={`mb-4 flex items-center gap-3 text-sm rounded-lg px-4 py-3 border ${
              flash.includes("Ошибка") || flash.includes("Не удалось")
                ? "bg-red-50 text-red-800 border-red-200"
                : "bg-green-100 text-green-800 border-green-200"
            }`}
          >
            <CheckCircleOutlineIcon
              className={flash.includes("Ошибка") ? "text-red-600" : "text-green-600"}
            />
            {flash}
          </div>
        )}

        <div
          className={`grid grid-cols-1 gap-8 transition-all duration-300 ease-in-out ${
            isPanelOpen ? "lg:grid-cols-2" : "lg:grid-cols-1"
          }`}
        >
          <div
            className={`space-y-4 ${mode === "create" ? "hidden lg:block" : ""} ${
              isPanelOpen ? "" : "lg:col-span-2"
            }`}
          >
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <SearchIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск по специалистам..."
                className="w-full rounded-md border-gray-300 bg-white py-2 pl-10 pr-10 shadow-sm focus:border-blue-500"
                disabled={!organizationId}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  <ClearIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>

            <div className="space-y-3">
              {loading ? (
                <div className="flex justify-center p-12">
                  <CircularProgress />
                </div>
              ) : loadError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
                  {loadError}
                </div>
              ) : !organizationId ? (
                <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
                  <h3 className="text-lg font-medium text-gray-500">
                    Организация не выбрана
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Выберите организацию, чтобы увидеть список специалистов.
                  </p>
                </div>
              ) : displayedSpecialists.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
                  <h3 className="text-lg font-medium text-gray-500">
                    {search ? "Ничего не найдено" : "Специалисты отсутствуют"}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {search
                      ? "Попробуйте изменить запрос."
                      : "Нажмите 'Добавить', чтобы создать первого."}
                  </p>
                </div>
              ) : (
                <>
                  {displayedSpecialists.map((spec) => (
                    <SpecialistListItem
                      key={spec.id}
                      specialist={spec}
                      onClick={() => openView(spec)}
                      isSelected={selected?.id === spec.id}
                    />
                  ))}
                  {/* Серверная пагинация */}
                  {totalPages > 1 && (
                    <AppPagination
                      page={page}
                      totalItems={totalItems}
                      pageSize={PAGE_SIZE}
                      onChange={setPage}
                      className="pt-2"
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {isPanelOpen && (
            <div className="rounded-lg border border-gray-200 bg-white shadow-lg h-fit">
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={closePanel}
                    className="inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:bg-gray-100 focus:outline-none"
                  >
                    <ArrowBackIcon />
                  </button>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {mode === "view" ? "Информация о специалисте" : mode === "edit" ? "Редактирование специалиста" : "Создание специалиста"}
                  </h2>
                </div>
              </div>

              <form onSubmit={onSave} noValidate>
                <div className="p-4 sm:p-6 space-y-4">
                  {loadError && (mode === "create" || mode === "edit") && (
                    <div className="flex items-center gap-3 text-sm text-red-800 bg-red-100 border border-red-200 rounded-lg px-4 py-3">
                      <ErrorOutlineIcon className="text-red-600" />
                      {loadError}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                    <FieldInput
                      className="md:col-span-2"
                      label="ФИО"
                      value={mode === "view" ? selected!.fullName : formData.fullName}
                      onChange={(v) => handleFormChange("fullName", v)}
                      error={errors.fullName}
                      required
                      disabled={mode === "view"}
                    />
                    <FieldInput
                      label="Email"
                      type="email"
                      value={mode === "view" ? (selected!.email ?? "") : formData.email}
                      onChange={(v) => handleFormChange("email", v)}
                      error={errors.email}
                      disabled={mode === "view"}
                    />
                    <FieldInput
                      label="Телефон"
                      value={mode === "view" ? (selected!.phone ?? "") : formData.phone}
                      onChange={(v) => handleFormChange("phone", v)}
                      disabled={mode === "view"}
                      placeholder="+7 (___) ___-__-__"
                      mask="+{7} (000) 000-00-00"
                      inputMode="tel"
                    />

                    {(mode === "create" || mode === "edit") && (
                      <FieldInput
                        className="md:col-span-2"
                        label="Организация"
                        value={cachedOrgName || (formData.organizationId ? `ID: ${formData.organizationId}` : "")}
                        disabled
                        error={errors.organizationId}
                      />
                    )}

                    {mode === "view" && selected?.createdAt && (
                      <FieldInput
                        className="md:col-span-2"
                        label="Дата создания"
                        value={formatDate(selected.createdAt)}
                        disabled
                      />
                    )}
                  </div>
                </div>

                {mode === "view" && (
                  <div className="flex justify-end p-4 bg-gray-50 rounded-b-lg border-t border-gray-200">
                    <button
                      type="button"
                      onClick={openEdit}
                      className="inline-flex justify-center items-center gap-2 rounded-md bg-white border border-gray-300 px-6 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 transition-colors"
                    >
                      <EditIcon fontSize="small" />
                      Редактировать
                    </button>
                  </div>
                )}

                {(mode === "create" || mode === "edit") && (
                  <div className="flex justify-end gap-3 p-4 bg-gray-50 rounded-b-lg border-t border-gray-200">
                    {mode === "edit" && (
                      <button
                        type="button"
                        onClick={() => openView(selected!)}
                        className="inline-flex justify-center gap-2 rounded-md bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50 transition-colors"
                      >
                        Отмена
                      </button>
                    )}
                    <button
                      type="submit"
                      className="inline-flex justify-center gap-2 rounded-md bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 transition-colors"
                    >
                      {mode === "create" ? "Сохранить" : "Сохранить изменения"}
                    </button>
                  </div>
                )}
              </form>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};